import {useState} from 'react';
import {UploadFileItem} from '../../data/models/LibraryFile/UploadFilesResponse';

export const useUploadQueue = (
  upload: (items: UploadFileItem[]) => Promise<void>,
) => {
  const [pendingVideos, setPendingVideos] = useState<File[]>([]);
  const [pendingOthers, setPendingOthers] = useState<File[]>([]);
  const [videoChoices, setVideoChoices] = useState<UploadFileItem[]>([]);
  const [restoredRes, setRestoredRes] = useState<number>();
  const [uploadingCount, setUploadingCount] = useState(0);

  const currentVideo = pendingVideos[0];

  const runUpload = async (items: UploadFileItem[]) => {
    if (!items.length) return;
    setUploadingCount(items.length);
    await upload(items);
    setUploadingCount(0);
  };

  const start = (files: File[]) => {
    const videos = files.filter(file => file.type.includes('video'));
    const rest = files.filter(file => !file.type.includes('video'));

    if (videos.length) {
      setPendingOthers(rest);
      setPendingVideos(videos);
      setVideoChoices([]);
      return;
    }
    runUpload(rest.map(file => ({file})));
  };

  const advance = async (collected: UploadFileItem[]) => {
    setRestoredRes(undefined);
    const remaining = pendingVideos.slice(1);
    if (remaining.length) {
      setVideoChoices(collected);
      setPendingVideos(remaining);
      return;
    }
    await runUpload([...pendingOthers.map(file => ({file})), ...collected]);
    setVideoChoices([]);
    setPendingVideos([]);
    setPendingOthers([]);
  };

  const onVideoConfigured = (duration: number, targetRes: number) =>
    advance([...videoChoices, {file: currentVideo, duration, targetRes}]);

  const cancel = () => {
    setVideoChoices([]);
    setPendingVideos([]);
    setPendingOthers([]);
  };

  const goToPreviousVideo = () => {
    const previous = videoChoices[videoChoices.length - 1];
    if (!previous) return;
    setVideoChoices(videoChoices.slice(0, -1));
    setPendingVideos([previous.file, ...pendingVideos]);
    setRestoredRes(previous.targetRes);
  };

  return {
    currentVideo,
    uploadingCount,
    start,
    cancel,
    onVideoConfigured,
    onPreviousVideo: videoChoices.length ? goToPreviousVideo : undefined,
    position: videoChoices.length + 1,
    total: videoChoices.length + pendingVideos.length,
    initialRes: restoredRes,
  };
};
